import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef
} from 'react';
import Navigation from '@components/Navigation';
import Footer from '@components/Footer';
import Section from './Section';
import useWindowSize from '@hooks/useWindowSize';
import styles from './Layout.module.scss';
import { mobileSize } from '@styles/variables.scss';
import { MediaModal } from '@components/Media';
import { Helmet } from 'react-helmet';
import { withPrefix } from 'gatsby';
import classNames from 'classnames';
import 'focus-visible';
import { isString, isPlainObject } from 'lodash';
import { getScrollbarWidth } from '@helpers';

const defaultState = {
  mobileMenuIsOpen: false,
  isMobile: false,
  hasMediaModal: false
};

export const LayoutContext = React.createContext(defaultState);

export default function Layout({
  title = 'Tripati Lab',
  description,
  overlayNav,
  showNavigation = true,
  showFooter = true,
  children
}) {
  const [state, setState] = useState(defaultState);
  const { width: windowWidth } = useWindowSize();
  const mediaModal = useRef();

  const updateLayout = useCallback(
    (value, name) => {
      setState(state => {
        if (isString(name) && !state.hasOwnProperty(name)) {
          return state;
        }

        if (name) {
          return { ...state, [name]: value };
        }

        if (isPlainObject(value)) {
          return { ...state, ...value };
        }

        return state;
      });
    },
    [setState]
  );

  useEffect(() => {
    updateLayout({ isMobile: windowWidth <= parseInt(mobileSize) });
  }, [windowWidth, updateLayout]);

  useEffect(() => {
    if (!state.isMobile) {
      updateLayout({
        mobileMenuIsOpen: false,
        scrollbarWidth: getScrollbarWidth()
      });
    }
  }, [state.isMobile, updateLayout]);

  useLayoutEffect(() => {
    document.activeElement?.getBoundingClientRect();
  }, []);

  useEffect(() => {
    let resizeTimer;

    const handler = () => {
      if (!resizeTimer) {
        document.body.classList.add('resizing');
      }

      clearTimeout(resizeTimer);

      resizeTimer = setTimeout(() => {
        resizeTimer = null;
        document.body.classList.remove('resizing');
      }, 100);
    };

    window.addEventListener('resize', handler);

    return () => window.removeEventListener('resize', handler);
  }, []);

  const contextValue = useMemo(() => {
    return { ...state, updateLayout, mediaModal };
  }, [state, updateLayout]);

  if (!title?.match(/tripati lab/i)) {
    title = `${title.trim()} | Tripati Lab`;
  }

  return (
    <LayoutContext.Provider value={contextValue}>
      <Helmet>
        <html lang="en" />
        <title>{title}</title>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover"
        />
        {description && <meta
          name="description"
          content={description}
        />}
        <link
          rel="icon"
          type="image/png"
          href={`${withPrefix('/')}images/favicon-32.png`}
          sizes="32x32"
        />
        <link
          rel="icon"
          type="image/png"
          href={`${withPrefix('/')}images/favicon-16.png`}
          sizes="16x16"
        />
        <meta
          name="theme-color"
          content="#4541E3"
        />
        <meta
          property="og:type"
          content="website"
        />
      </Helmet>
      <div id="page-wrapper">
        {showNavigation && <Navigation overlayNav={overlayNav} />}
        <main id="main-content">{children}</main>
        {showFooter && <Footer />}
      </div>
      {state.hasMediaModal && <MediaModal ref={mediaModal} />}
    </LayoutContext.Provider>
  );
}

export function Content({
  className,
  maxWidth,
  style = {},
  children,
  ...restProps
}) {
  return (
    <div
      className={classNames(styles.contentWrapper, className)}
      style={{ maxWidth, ...style }}
      {...restProps}
    >
      {children}
    </div>
  );
}

export const MainContent = React.forwardRef(
  (
    {
      className,
      contentClassName,
      contentStyle,
      children,
      rightAlignDots = false,
      showDots = true,
      style = {},
      maxWidth,
      ...restProps
    },
    ref
  ) => {
    return (
      <Section
        className={classNames(styles.main, className, {
          [styles.rightDots]: rightAlignDots,
          [styles.hideDots]: !showDots
        })}
        ref={ref}
        style={{ ...style, maxWidth }}
        {...restProps}
      >
        <div
          className={classNames(styles.content, contentClassName)}
          style={contentStyle}
        >
          {children}
        </div>
      </Section>
    );
  }
);

export function FillContainer({
  tag: Tag = 'div',
  className,
  showDots,
  rightAlignDots,
  ...restProps
}) {
  const props = {
    className: classNames(styles.fillContainer, className),
    ...restProps
  };

  if (Tag === 'section') {
    props.showDots = showDots;
    props.rightAlignDots = rightAlignDots;

    return <Section {...props} />;
  }

  return <Tag {...props} />;
}
